import Typography from "components/typography";
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";

interface IProps {
  width?: string;
  height?: string;
  setFile: (file: File) => void;
  type?: "image" | "audio" | "doc" | "video";
  limit?: number; // in MB
  subheader?: boolean;
}
const DragNDrop = ({
  width = "100%",
  height = "100%",
  setFile,
  type = "image",
  limit = 1,
  subheader = true
}: IProps) => {
  const [dragEnter, setDragEnter] = useState(false);
  const inputRef = useRef(null);
  const handleDragOver = (e: { preventDefault: () => void }) => {
    e.preventDefault();
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const size = Number((file.size / 1024 / 1024).toFixed(4));

    if (file && size < limit) {
      setFile(file);
    }
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];

    if (!file) {
      return;
    }

    const size = Number(file.size / 1024 / 1024);

    if (file && size < limit) {
      setFile(file);
    }
  };

  const ComponentProperties = {
    image: {
      accept: "image/png, image/jpeg, image/jpg",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          viewBox="0 0 21 20"
          fill="none"
        >
          <path
            d="M15.5013 6.66667C15.5013 7.58714 14.7551 8.33333 13.8346 8.33333C12.9141 8.33333 12.168 7.58714 12.168 6.66667C12.168 5.74619 12.9141 5 13.8346 5C14.7551 5 15.5013 5.74619 15.5013 6.66667Z"
            fill="#4E4E4E"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M10.4535 1.0415H10.5491C12.4728 1.0415 13.9803 1.04149 15.1566 1.19963C16.3605 1.3615 17.3106 1.69928 18.0562 2.44495C18.8019 3.19063 19.1396 4.14065 19.3016 5.3446C19.4596 6.52083 19.4596 8.02836 19.4596 9.952V10.0256C19.4596 11.6163 19.4596 12.9184 19.3732 13.9786C19.2865 15.0441 19.1086 15.9343 18.7104 16.6738C18.5347 16.9999 18.319 17.292 18.0562 17.5548C17.3106 18.3004 16.3605 18.6382 15.1566 18.8001C13.9803 18.9582 12.4728 18.9582 10.5491 18.9582H10.4535C8.52983 18.9582 7.02229 18.9582 5.84606 18.8001C4.64211 18.6382 3.69209 18.3004 2.94642 17.5548C2.28535 16.8937 1.94397 16.0708 1.76243 15.0502C1.58409 14.0476 1.55147 12.8001 1.54469 11.2511C1.54297 10.8571 1.54297 10.4403 1.54297 10.0007V9.952C1.54296 8.02837 1.54295 6.52083 1.70109 5.3446C1.86296 4.14065 2.20074 3.19063 2.94642 2.44495C3.69209 1.69928 4.64211 1.3615 5.84606 1.19963C7.02229 1.04149 8.52984 1.0415 10.4535 1.0415ZM6.01262 2.43848C4.94728 2.58171 4.30501 2.85412 3.8303 3.32884C3.35559 3.80355 3.08318 4.44581 2.93994 5.51115C2.79429 6.59447 2.79297 8.01799 2.79297 9.99984C2.79297 10.2419 2.79297 10.4761 2.79325 10.7028L3.62754 9.97275C4.38693 9.30834 5.53144 9.34642 6.24495 10.0599L9.81972 13.6347C10.3924 14.2074 11.2939 14.2855 11.9566 13.8198L12.2051 13.6452C13.1586 12.975 14.4486 13.0527 15.315 13.8323L17.6736 15.9551C17.9111 15.4565 18.0521 14.8013 18.1274 13.8771C18.2091 12.8733 18.2096 11.6214 18.2096 9.99984C18.2096 8.01799 18.2083 6.59447 18.0626 5.51115C17.9195 4.44581 17.6471 3.80355 17.1723 3.32884C16.6976 2.85412 16.0553 2.58171 14.99 2.43848C13.9066 2.29283 12.4831 2.2915 10.5013 2.2915C8.51945 2.2915 7.09594 2.29283 6.01262 2.43848Z"
            fill="#4E4E4E"
          />
        </svg>
      ),
      header: "Select an image",
      subheader: "Or drag image here to upload"
    },
    audio: {
      accept: "audio/mpeg, audio/wav, audio/ogg, audio/mp3",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M7.08853 1.66651H12.9053C13.099 1.66646 13.2475 1.66643 13.3774 1.67913C14.3005 1.76944 15.0562 2.32449 15.3766 3.07215H4.61719C4.93761 2.32449 5.69325 1.76944 6.61643 1.67913C6.74626 1.66643 6.8948 1.66646 7.08853 1.66651Z"
            fill="#4E4E4E"
          />
          <path
            d="M5.25769 3.93604C4.09883 3.93604 3.14862 4.63583 2.83151 5.56419C2.8249 5.58354 2.81856 5.603 2.8125 5.62254C3.14429 5.52207 3.4896 5.45643 3.83915 5.41162C4.73947 5.29619 5.87725 5.29625 7.19894 5.29633H12.9423C14.264 5.29625 15.4018 5.29619 16.3022 5.41162C16.6517 5.45643 16.997 5.52207 17.3288 5.62254C17.3228 5.603 17.3164 5.58354 17.3098 5.56419C16.9927 4.63583 16.0424 3.93604 14.8836 3.93604H5.25769Z"
            fill="#4E4E4E"
          />
          <path
            d="M9.375 14.1665C9.375 13.8213 9.09517 13.5415 8.75 13.5415C8.40483 13.5415 8.125 13.8213 8.125 14.1665C8.125 14.5117 8.40483 14.7915 8.75 14.7915C9.09517 14.7915 9.375 14.5117 9.375 14.1665Z"
            fill="#4E4E4E"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M7.22829 6.28516H12.7743C15.5866 6.28516 16.9928 6.28516 17.7827 7.10751C18.5726 7.92986 18.3867 9.20046 18.0151 11.7415L17.6626 14.151C17.3711 16.1438 17.2254 17.1401 16.4777 17.7368C15.7301 18.3335 14.6273 18.3335 12.4218 18.3335H7.58075C5.3753 18.3335 4.27258 18.3335 3.52492 17.7368C2.77726 17.1401 2.63152 16.1438 2.34004 14.151L1.98756 11.7415C1.61587 9.20037 1.43003 7.92987 2.2199 7.10751C3.00978 6.28516 4.41595 6.28516 7.22829 6.28516ZM10.6263 8.75012C10.6263 8.40496 10.3465 8.12512 10.0013 8.12512C9.65614 8.12512 9.3763 8.40496 9.3763 8.75012V12.3985C9.1808 12.3294 8.97047 12.2918 8.7513 12.2918C7.71577 12.2918 6.8763 13.1313 6.8763 14.1668C6.8763 15.2023 7.71577 16.0418 8.7513 16.0418C9.7868 16.0418 10.6263 15.2023 10.6263 14.1668V11.0999C11.1848 11.553 11.8861 11.8751 12.5013 11.8751C12.8465 11.8751 13.1263 11.5953 13.1263 11.2501C13.1263 10.905 12.8465 10.6251 12.5013 10.6251C12.2468 10.6251 11.7634 10.443 11.3209 10.0504C10.8891 9.66729 10.6263 9.19896 10.6263 8.75012Z"
            fill="#4E4E4E"
          />
        </svg>
      ),
      header: "Select an audio file",
      subheader: "Or drag audio file here to upload"
    },
    doc: {
      accept: ".doc,.docx,.txt",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="21"
          viewBox="0 0 20 21"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3.47631 2.74877C2.5 3.72509 2.5 5.29643 2.5 8.43913V11.7725C2.5 14.9151 2.5 16.4865 3.47631 17.4628C4.45262 18.4391 6.02397 18.4391 9.16667 18.4391H10.8333C13.976 18.4391 15.5474 18.4391 16.5237 17.4628C17.5 16.4865 17.5 14.9151 17.5 11.7725V8.43913C17.5 5.29643 17.5 3.72509 16.5237 2.74877C15.5474 1.77246 13.976 1.77246 10.8333 1.77246H9.16667C6.02397 1.77246 4.45262 1.77246 3.47631 2.74877ZM6.04167 6.77246C6.04167 6.42729 6.32149 6.14746 6.66667 6.14746H13.3333C13.6785 6.14746 13.9583 6.42729 13.9583 6.77246C13.9583 7.11764 13.6785 7.39746 13.3333 7.39746H6.66667C6.32149 7.39746 6.04167 7.11764 6.04167 6.77246ZM6.04167 10.1058C6.04167 9.76063 6.32149 9.48079 6.66667 9.48079H13.3333C13.6785 9.48079 13.9583 9.76063 13.9583 10.1058C13.9583 10.451 13.6785 10.7308 13.3333 10.7308H6.66667C6.32149 10.7308 6.04167 10.451 6.04167 10.1058ZM6.66667 12.8141C6.32149 12.8141 6.04167 13.094 6.04167 13.4391C6.04167 13.7843 6.32149 14.0641 6.66667 14.0641H10.8333C11.1785 14.0641 11.4583 13.7843 11.4583 13.4391C11.4583 13.094 11.1785 12.8141 10.8333 12.8141H6.66667Z"
            fill="#4E4E4E"
          />
        </svg>
      ),
      header: "Select a .docx / .txt  file",
      subheader: "Or drag .docx / .txt file here to upload"
    },
    video: {
      accept: "video/*",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M7.09146 1.66651H12.9082C13.102 1.66646 13.2505 1.66643 13.3803 1.67913C14.3035 1.76944 15.0591 2.32448 15.3796 3.07215H4.62012C4.94054 2.32448 5.69618 1.76944 6.61936 1.67913C6.74919 1.66643 6.89773 1.66646 7.09146 1.66651Z"
            fill="#4E4E4E"
          />
          <path
            d="M5.25867 3.93555C4.09981 3.93555 3.14959 4.63534 2.83248 5.5637C2.82588 5.58305 2.81953 5.60251 2.81348 5.62206C3.14527 5.52158 3.49058 5.45594 3.84013 5.41113C4.74044 5.29571 5.87823 5.29576 7.19992 5.29584H12.9433C14.265 5.29576 15.4028 5.29571 16.3032 5.41113C16.6527 5.45594 16.998 5.52158 17.3297 5.62206C17.3237 5.60251 17.3174 5.58305 17.3107 5.5637C16.9937 4.63534 16.0434 3.93555 14.8846 3.93555H5.25867Z"
            fill="#4E4E4E"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.7728 6.28516H7.22683C4.41449 6.28516 3.00831 6.28516 2.21844 7.10751C1.42856 7.92987 1.6144 9.20037 1.9861 11.7415L2.33857 14.151C2.63005 16.1438 2.7758 17.1401 3.52345 17.7368C4.27111 18.3335 5.37384 18.3335 7.57929 18.3335H12.4203C14.6258 18.3335 15.7286 18.3335 16.4763 17.7368C17.2239 17.1401 17.3696 16.1438 17.6611 14.151L18.0136 11.7415C18.3853 9.20046 18.5711 7.92986 17.7813 7.10751C16.9913 6.28516 15.5852 6.28516 12.7728 6.28516ZM12.1508 13.162C12.6162 12.8735 12.6162 12.1267 12.1508 11.8383L9.34117 10.0965C8.88892 9.81612 8.33317 10.181 8.33317 10.7584V14.2419C8.33317 14.8192 8.88892 15.1841 9.34117 14.9038L12.1508 13.162Z"
            fill="#4E4E4E"
          />
        </svg>
      ),
      header: "Select a video file",
      subheader: "Or drag video file here to upload"
    }
  };

  return (
    <Container
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnter={() => setDragEnter(true)}
      onDragLeave={() => setDragEnter(false)}
      style={{ width: width, height: height }}
    >
      <FormInput
        type="file"
        accept={ComponentProperties[type].accept}
        onChange={handleFileChange}
        ref={inputRef}
        multiple={false}
      />
      <ImageIcon>{ComponentProperties[type].icon}</ImageIcon>
      <Typography color={Colors.yellow400} size="xs" weight="semibold" mb="0.4">
        {ComponentProperties[type].header}
      </Typography>
      {subheader && (
        <Typography color={Colors.grey400} size="xs">
          {ComponentProperties[type].subheader}
        </Typography>
      )}
    </Container>
  );
};

export default DragNDrop;

const Container = styled.form`
  cursor: pointer;
  width: 24.6rem;
  height: 24.6rem;
  border-radius: 8px;
  border: 0.5px dashed ${Colors.yellow300};
  background: rgba(254, 198, 103, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
`;

const ImageIcon = styled.div`
  width: 2rem;
  height: 2rem;
  margin-bottom: 0.4rem;
`;

const FormInput = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0;
  cursor: pointer;
`;
